import React from 'react'
import { createPortal } from 'react-dom'
import { ApiError, Appointment, ValidationError, VersionMismatchError, api } from '../api/client'
import { formatPrice, parsePrice, priceInput } from '../catalog/format'
import { saveErrorMessage } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import Field from '../screens/settings/Field'
import { discountProblem } from './discount'
import './Sheet.css'
import '../screens/settings/Settings.css'

interface DiscountSheetProps {
  appointment: Appointment
  onClose: () => void
  onSaved: () => void
  // onStale: the booking changed or can no longer take a discount; reload it.
  onStale: (message: string) => void
}

// DiscountSheet gives one appointment a one-off discount (£ or %, with an
// optional note) or a code. It saves with If-Match and says why when refused.
export default function DiscountSheet({ appointment, onClose, onSaved, onStale }: DiscountSheetProps) {
  const [useCode, setUseCode] = React.useState(false)
  const [kind, setKind] = React.useState<'percent' | 'amount'>('amount')
  const [value, setValue] = React.useState('')
  const [note, setNote] = React.useState('')
  const [code, setCode] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const save = async () => {
    let body: Parameters<typeof api.setDiscount>[2]
    if (useCode) {
      if (!code.trim()) {
        setError('Enter a code.')
        return
      }
      body = { code: code.trim() }
    } else {
      const n = kind === 'percent' ? (/^\d{1,3}$/.test(value.trim()) ? Number(value.trim()) : null) : parsePrice(value)
      if (n === null || n < 1 || (kind === 'percent' && n > 100)) {
        setError(kind === 'percent' ? 'Choose a percentage between 1 and 100.' : 'Enter an amount of at least 1p.')
        return
      }
      body = { discount: { kind, value: n, ...(note.trim() ? { note: note.trim() } : {}) } }
    }
    setBusy(true)
    setError('')
    try {
      await api.setDiscount(appointment.id, appointment.version, body)
      onSaved()
      return
    } catch (e) {
      if (e instanceof VersionMismatchError) {
        onStale('This booking changed on another device. Check it and try again.')
        return
      } else if (e instanceof ApiError && e.problem === 'appointment-state') {
        onStale("This booking can’t take a discount any more.")
        return
      } else if (e instanceof ApiError && (e.problem === 'discount-code' || e.problem === 'discount-below-paid')) {
        setError(discountProblem(e, appointment.paid_pence))
      } else if (e instanceof ValidationError) {
        setError(Object.values(e.fields)[0] ?? 'Check the details and try again.')
      } else {
        setError(saveErrorMessage(e))
      }
    }
    setBusy(false)
  }

  const sheet = (
    <div className="sheet-overlay" onClick={(e) => { e.stopPropagation(); if (!busy) onClose() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Discount" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Discount</h2>
          <p className="field-hint">Price {formatPrice(appointment.discount?.was_pence ?? appointment.total_price_pence)}</p>
        </div>
        <div className="sheet-body">
          <div className="settings-form sheet-form">
            {useCode ? (
              <>
                <Field id="discount-code" label="Code" error={error}>
                  <input id="discount-code" autoCapitalize="characters" autoComplete="off" value={code} disabled={busy}
                    onChange={(e) => { setCode(e.target.value); setError('') }} />
                </Field>
                <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setUseCode(false); setError('') }}>Give a one-off discount</button>
              </>
            ) : (
              <>
                <div className="segmented discount-kind" role="group" aria-label="Discount type">
                  <button type="button" aria-pressed={kind === 'amount'} onClick={() => { setKind('amount'); setError('') }}>£ off</button>
                  <button type="button" aria-pressed={kind === 'percent'} onClick={() => { setKind('percent'); setError('') }}>% off</button>
                </div>
                <Field id="discount-value" label={kind === 'percent' ? 'Percentage off' : 'Amount off (£)'} error={error}>
                  <input id="discount-value" inputMode="decimal" value={value} disabled={busy} placeholder={kind === 'percent' ? '10' : priceInput(500)}
                    onChange={(e) => { setValue(e.target.value); setError('') }} />
                </Field>
                <Field id="discount-note" label="Note (optional)">
                  <input id="discount-note" maxLength={60} placeholder="Friend" value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
                </Field>
                <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setUseCode(true); setError('') }}>Use a code</button>
              </>
            )}
          </div>
        </div>
        <div className="sheet-actions">
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Apply'}</button>
        </div>
        <button type="button" className="btn btn-quiet btn-block" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
